// Shared by the storefront and the Shopify configuration editor.
export const PAGE_FEATURES = ['artwork', 'proof', 'reviews', 'benefits', 'delivery', 'sticky', 'paymentIcons', 'directCheckout'];
export const MAX_CUSTOM_OPTIONS = 8;
const reservedProperties = new Set(['artwork', 'artwork status', 'artwork proof', 'pieces per batch', '_design request', 'selling unit']);
const QUOTE_ENCODING = 'indexed-v1';
const CONFIG_LIMIT = 128 * 1024;
const jsonBytes = value => new TextEncoder().encode(value).length;

export function decodeProductConfiguration(config = {}) {
  if (!config.quoteEncoding) return config;
  if (config.quoteEncoding !== QUOTE_ENCODING || config.version !== 2 || !Array.isArray(config.options) || !Array.isArray(config.quotes) || !Array.isArray(config.quoteNativeOptions)) throw new Error('Invalid quotation encoding.');
  const {quoteEncoding, quoteNativeOptions, ...decoded} = config;
  const indexed = (list, index) => {
    if (!Array.isArray(list) || !Number.isSafeInteger(index) || index < 0 || index >= list.length) throw new Error('Invalid quotation index.');
    return list[index];
  };
  decoded.quotes = config.quotes.map(row => {
    if (!Array.isArray(row) || row.length < 6 || row.length > 7 || !Array.isArray(row[4]) || row[4].length !== config.options.length || row[5] !== null && !Array.isArray(row[5])) throw new Error('Invalid quotation row.');
    return {...row[6], quantity: row[0], cents: row[1],
      ...(row[2] !== null ? {variantId: row[2]} : {}), ...(row[3] !== null ? {sku: row[3]} : {}),
      values: Object.fromEntries(config.options.map((option, i) => [option.id, indexed(option.values, row[4][i]).id])),
      ...(row[5] !== null ? {nativeOptions: row[5].map(index => ({...indexed(quoteNativeOptions, index)}))} : {}),
    };
  });
  return decoded;
}

// Keep ordinary configurations unchanged; large matrices share option text by index.
export function serializeProductConfiguration(input) {
  const config = decodeProductConfiguration(input);
  let value = JSON.stringify(config);
  if (jsonBytes(value) <= CONFIG_LIMIT) return value;
  if (config.version === 2 && Array.isArray(config.options) && Array.isArray(config.quotes)) {
    const quoteNativeOptions = [], nativeIndexes = new Map();
    const quotes = config.quotes.map(({quantity, cents, variantId, sku, values, nativeOptions, ...extra}) => {
      const indices = config.options.map(option => {
        const index = option.values.findIndex(value => value.id === values?.[option.id]);
        if (index < 0) throw new Error('Invalid quotation option.');
        return index;
      });
      const native = nativeOptions ? nativeOptions.map(option => {
        const key = JSON.stringify(option);
        if (!nativeIndexes.has(key)) { nativeIndexes.set(key, quoteNativeOptions.length); quoteNativeOptions.push(option); }
        return nativeIndexes.get(key);
      }) : null;
      const row = [quantity, cents, variantId ?? null, sku ?? null, indices, native];
      if (Object.keys(extra).length) row.push(extra);
      return row;
    });
    value = JSON.stringify({...config, quoteEncoding: QUOTE_ENCODING, quoteNativeOptions, quotes});
  }
  if (jsonBytes(value) > CONFIG_LIMIT) throw new Error('报价配置精简后仍超过 128 KB，请缩短内容或拆分商品。');
  return value;
}

export function productMode(config = {}) {
  if (config?.sync?.state === 'pending') throw new Error('Batch quotations are being synchronized.');
  const requested = config?.page?.mode || config?.mode || 'auto';
  if (requested === 'native') return 'native';
  if (requested === 'batch' && !config?.quotes?.length) throw new Error('Batch ordering requires a configured quotation matrix.');
  return config?.quotes?.length ? 'batch' : 'native';
}

export function pageSettings(config = {}) {
  const mode = productMode(config), page = config?.page || {};
  const features = Object.fromEntries(PAGE_FEATURES.map(key => [key,
    typeof page.features?.[key] === 'boolean' ? page.features[key]
      : key === 'directCheckout' ? mode === 'native' : ['sticky', 'paymentIcons'].includes(key) || mode === 'batch']));
  features.delivery &&= !!(page.delivery || config?.delivery)?.enabled;
  features.directCheckout &&= mode === 'native' && !features.artwork && !features.proof;
  return {...page, mode, features, optionLabels: Array.from({length: Math.max(3, config.options?.length || 0)}, (_, i) => String(page.optionLabels?.[i] || ''))};
}

export function validatePageSettings(page, config = {}) {
  if (!page || !['auto', 'native', 'batch'].includes(page.mode)) throw new Error('Choose an automatic, native or batch ordering mode.');
  if (page.mode === 'batch' && !config.quotes?.length) throw new Error('Batch ordering requires quotations. Import the quotation matrix first.');
  for (const key of PAGE_FEATURES) if (typeof page.features?.[key] !== 'boolean') throw new Error(`Invalid feature setting: ${key}`);
  if (!Array.isArray(page.optionLabels) || page.optionLabels.length > MAX_CUSTOM_OPTIONS || page.optionLabels.some(label => typeof label !== 'string' || label.length > 80)) throw new Error('Option labels must be at most 80 characters, with at most eight options.');
  const labels = page.optionLabels.map(label => label.trim().toLowerCase()).filter(Boolean);
  if (new Set(labels).size !== labels.length || labels.some(label => reservedProperties.has(label) || /[\[\]\r\n]/.test(label))) throw new Error('Option labels must be unique and cannot use reserved order fields.');
  for (const key of ['eyebrow', 'quantityLabel', 'quoteText']) if (page[key] !== undefined && (typeof page[key] !== 'string' || page[key].length > 200)) throw new Error(`Invalid text setting: ${key}`);
  for (const [key, fields] of [['specifications', ['label', 'value']], ['faq', ['question', 'answer']]]) {
    if (page[key] && (!Array.isArray(page[key]) || page[key].length > 40 || page[key].some(row => fields.some(field => typeof row[field] !== 'string' || !row[field].trim() || row[field].length > 4000)))) throw new Error(`Invalid ${key} rows.`);
  }
  if (page.delivery?.enabled) {
    for (const range of [page.delivery.production, ...(page.delivery.methods || []).map(method => method.days)]) if (!range || !Number.isSafeInteger(range.min) || !Number.isSafeInteger(range.max) || range.min < 0 || range.max < range.min || range.max > 999) throw new Error('Enter valid production and transit working days.');
    if (!page.delivery.methods?.length) throw new Error('Add at least one delivery method.');
  }
  return page;
}

export function productConfiguration(raw, native, artwork = {}) {
  let config = decodeProductConfiguration(raw || {});
  if (typeof artwork.enabled === 'boolean' || typeof artwork.required === 'boolean' || Number.isInteger(artwork.maxMB) && artwork.maxMB >= 1 && artwork.maxMB <= 10) {
    const page = {...config.page}, upload = {...config.upload, ...page.upload};
    if (typeof artwork.enabled === 'boolean') page.features = {...page.features, artwork: artwork.enabled};
    if (typeof artwork.required === 'boolean') upload.required = artwork.required;
    if (Number.isInteger(artwork.maxMB) && artwork.maxMB >= 1 && artwork.maxMB <= 10) upload.maxBytes = artwork.maxMB * 1048576;
    config = {...config, page: {...page, upload}};
  }
  const page = pageSettings(config);
  if (page.mode === 'native') {
    if (!native || !/^[A-Z]{3}$/.test(native.currency || '')) throw new Error('Native product data is missing.');
    return {...config, upload: config.page?.upload || config.upload, delivery: config.page?.delivery || config.delivery, page, mode: 'native', currency: native.currency,
      options: (native.options || []).map((option, i) => ({...option, label: page.optionLabels[i] || option.label, kind: 'generic', values: option.values.map(value => ({...value, id: String(value.id)}))})),
      native, quotes: [],
    };
  }
  let options, quotes;
  if (config.version === 1) {
    options = [
      {id: 'size', label: 'Size', kind: 'size', values: config.sizes || []},
      {id: 'lid', label: 'Lid Type', kind: 'lid', values: config.lids || []},
      {id: 'color', label: 'Lid Color', kind: 'color', values: [{id: 'none', label: 'Not applicable'}, ...(config.colors || [])], when: {option: 'lid', not: 'none'}, inactiveValue: 'none'},
    ];
    quotes = (config.quotes || []).map(quote => ({...quote, values: {size: quote.size, lid: quote.lid, color: quote.color}}));
  } else {
    options = config.options || []; quotes = config.quotes || [];
  }
  if (!options.length || options.length > MAX_CUSTOM_OPTIONS || !quotes.length || !/^[A-Z]{3}$/.test(config.currency || '')) throw new Error('Invalid quotation configuration.');
  const keys = new Set(), ids = options.map(option => option.id);
  if (new Set(ids).size !== ids.length) throw new Error('Duplicate product option.');
  for (const quote of quotes) {
    if (!Number.isSafeInteger(quote.cents) || quote.cents <= 0 || !Number.isSafeInteger(quote.quantity) || quote.quantity <= 0) throw new Error('Invalid quotation price or order size.');
    for (const option of options) if (!option.values.some(value => value.id === quote.values?.[option.id])) throw new Error('Unknown quotation option.');
    const key = JSON.stringify([ids.map(id => quote.values[id]), quote.quantity]);
    if (keys.has(key)) throw new Error('Duplicate quotation combination.'); keys.add(key);
  }
  options = options.map((option, i) => ({...option, label: page.optionLabels[i] || option.label,
    kind: page.optionLabels[i] && page.optionLabels[i] !== option.label ? 'generic' : option.kind}));
  return {...config, upload: config.page?.upload || config.upload, delivery: config.page?.delivery || config.delivery, page, mode: 'batch', options, quotes};
}

export function selectBatch(config, requested = {}) {
  const values = {}, states = [];
  let candidates = config.quotes;
  for (const option of config.options) {
    const active = !option.when || values[option.when.option] !== option.when.not;
    const available = option.values.filter(value => candidates.some(quote => quote.values[option.id] === value.id));
    const value = active ? (available.find(value => value.id === requested.values?.[option.id]) || available[0])?.id : option.inactiveValue;
    if (value === undefined) return null;
    values[option.id] = value;
    candidates = candidates.filter(quote => quote.values[option.id] === value);
    states.push({...option, active, values: available, value});
  }
  const quotes = [...candidates].sort((a, b) => a.quantity - b.quantity);
  const quote = quotes.find(quote => quote.quantity === Number(requested.quantity)) || quotes[0];
  return quote ? {values, states, quotes, quote, quantity: quote.quantity} : null;
}

export function nativeQuote(native) {
  const selected = native?.selected;
  return selected ? {...selected, variantId: String(selected.id), cents: selected.price, quantity: 1, values: selected.values || {}} : null;
}

export function orderQuantity(value, rule = {}) {
  const min = Number(rule.min) || 1, increment = Number(rule.increment) || 1;
  const amount = Number(value);
  return Number.isSafeInteger(amount) && amount >= min && (amount - min) % increment === 0 && (!rule.max || amount <= rule.max);
}

export function normalizeOrderQuantity(value, rule = {}) {
  const min = Number(rule.min) || 1, increment = Number(rule.increment) || 1;
  const desired = Math.max(min, Math.min(Number(rule.max) || Infinity, Math.floor(Number(value)) || min));
  return min + Math.floor((desired - min) / increment) * increment;
}

export function unitPrice(quote, quantity) {
  return (quote.priceBreaks || []).filter(row => quantity >= row.minimum).sort((a, b) => a.minimum - b.minimum).reduce((cents, row) => row.price, quote.cents);
}
